import React from "react";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const pricingPlans = [{
  duration: "Individual Monthly",
  durationEn: "Monthly Flex",
  price: "$29",
  pricePerMonth: "Billed monthly",
  popular: false,
  features: [{
    cz: "Soukromý přístup přes iMessage",
    en: "Private iMessage access"
  }, {
    cz: "Základní orchestrace AI",
    en: "Basic AI orchestration"
  }, {
    cz: "Rychlost standardní odezvy",
    en: "Standard response speed"
  }, {
    cz: "Podpora přes e-mail",
    en: "Email support"
  }]
}, {
  duration: "Individual Pro",
  durationEn: "Annual Access",
  price: "$19",
  pricePerMonth: "Billed annually ($228/yr)",
  popular: true,
  features: [{
    cz: "Vše z tarifu Individual",
    en: "Everything in Individual"
  }, {
    cz: "Prioritní přístup k serverům",
    en: "Priority server access"
  }, {
    cz: "Integrace do Slacku",
    en: "Slack integration"
  }, {
    cz: "Přednostní nové funkce",
    en: "Early access to features"
  }]
}, {
  duration: "Advanced",
  durationEn: "Power User",
  price: "$49",
  pricePerMonth: "Billed monthly",
  popular: false,
  features: [{
    cz: "Vše z tarifu Pro",
    en: "Everything in Pro"
  }, {
    cz: "Vlastní konfigurační profily",
    en: "Custom config profiles"
  }, {
    cz: "Rozšířená paměť kontextu",
    en: "Extended context memory"
  }, {
    cz: "Dedikovaná linka podpory",
    en: "Dedicated support line"
  }]
}, {
  duration: "Business",
  durationEn: "Team Launch",
  price: "$99",
  pricePerMonth: "Starting at 5 users",
  popular: false,
  features: [{
    cz: "Správa týmových licencí",
    en: "Team license management"
  }, {
    cz: "Sdílené znalostní báze",
    en: "Shared knowledge bases"
  }, {
    cz: "Administrátorský panel",
    en: "Admin dashboard"
  }, {
    cz: "SLA garantovaná odezva",
    en: "SLA guaranteed response"
  }]
}];
const singleEntry = {
  title: "Zkušební přístup",
  titleEn: "Trial Access",
  price: "Free",
  features: [{
    cz: "24hodinový přístup",
    en: "24-hour access"
  }, {
    cz: "Omezený počet dotazů",
    en: "Limited query count"
  }, {
    cz: "Základní funkce AI",
    en: "Basic AI capabilities"
  }]
};
export default function MembershipPricing() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="38631" id="straightforward-access" ref={ref} className="dark bg-background py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        {}
        <div className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-4xl md:text-5xl font-default font-bold mb-4 text-foreground uppercase tracking-tighter">
            Straightforward Access
          </h2>
          <p className="text-xl text-muted-foreground font-default">
            Full-scale AI capability in under a minute.
            <span className="block text-sm font-default font-normal mt-1 opacity-70 italic">
              No apps. No APIs. Just answers.
            </span>
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
          {pricingPlans.map((plan, i) => <div key={i} data-index={i} className={`h-full transition-all duration-700 delay-[${i * 100}ms] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
              <Card className={`h-full flex flex-col border-2 rounded-3xl relative overflow-hidden transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl ${plan.popular ? "border-primary bg-card shadow-xl" : "border-border/40 bg-card/40 hover:bg-card"}`}>
                {plan.popular && <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-[10px] font-bold px-4 py-1.5 rounded-bl-2xl z-10 tracking-widest uppercase">
                    Recommended
                  </div>}
                <CardHeader className="pb-6 text-center border-b border-border/20 bg-muted/10">
                  <CardTitle className="text-lg font-default font-bold uppercase tracking-widest text-muted-foreground">
                    {plan.duration}
                  </CardTitle>
                  <CardDescription className="text-xs font-default uppercase tracking-widest opacity-60">
                    {plan.durationEn}
                  </CardDescription>
                  <div className="mt-6">
                    <span className="text-7xl font-default font-bold text-foreground block tracking-tighter">
                      {plan.price}
                    </span>
                    <span className="text-xs font-default text-muted-foreground font-medium uppercase tracking-wider">
                      {plan.pricePerMonth}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="flex-grow pt-8">
                  <ul className="space-y-4">
                    {plan.features.map((feature, idx) => <li key={idx} data-index={idx} className="flex items-start gap-3 text-sm text-muted-foreground">
                        <Check className="h-5 w-5 text-primary shrink-0" />
                        <span className="leading-tight font-default">
                          <span className="font-medium text-foreground">
                            {feature.cz}
                          </span>
                          <span className="block text-xs opacity-60 mt-0.5">
                            {feature.en}
                          </span>
                        </span>
                      </li>)}
                  </ul>
                </CardContent>
                <CardFooter className="pt-4 pb-8 px-8">
                  <Button asChild className={`w-full font-default font-bold rounded-xl py-6 transition-all ${plan.popular ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20" : "bg-secondary text-secondary-foreground"}`} variant={plan.popular ? "default" : "secondary"}>
                    <Link to="#">
                      Get Started
                      <span className="ml-1 text-[10px] opacity-70 font-normal uppercase tracking-tighter">
                        / Start Now
                      </span>
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>

        {}
        <div className="text-center mb-16 italic text-muted-foreground text-sm font-default">
          Enterprise deployments available upon request for organizations requiring custom security.
        </div>

        {}
        <div className={`grid grid-cols-1 lg:grid-cols-3 gap-8 items-center max-w-5xl mx-auto transition-all duration-700 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {}
          <Card className="lg:col-span-1 border-border/40 bg-card/30 rounded-3xl backdrop-blur-sm">
            <CardHeader className="pb-2 text-center">
              <CardTitle className="text-md font-default font-bold uppercase tracking-widest text-muted-foreground">
                {singleEntry.title}
              </CardTitle>
              <CardDescription className="text-xs font-default uppercase tracking-widest opacity-60">
                {singleEntry.titleEn}
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center pb-6">
              <div className="text-4xl font-default font-bold text-primary mb-6 tracking-tighter">
                {singleEntry.price}
              </div>
              <ul className="space-y-3 text-left inline-block mx-auto">
                {singleEntry.features.map((feature, idx) => <li key={idx} data-index={idx} className="flex items-center gap-2 text-xs text-muted-foreground font-default">
                    <Check className="h-4 w-4 text-primary/60" />
                    <span>
                      {feature.cz}{" "}
                      <span className="opacity-50">/ {feature.en}</span>
                    </span>
                  </li>)}
              </ul>
            </CardContent>
            <CardFooter className="px-6 pb-6">
              <Button asChild variant="outline" className="w-full rounded-xl border-border/40 font-default">
                <Link to="#">Try Now / Vyzkoušet</Link>
              </Button>
            </CardFooter>
          </Card>

          {}
          <div className="lg:col-span-2 text-center lg:text-left bg-primary/5 rounded-3xl p-10 border border-primary/10 backdrop-blur-md">
            <h3 className="text-3xl font-default font-bold mb-4 tracking-tight">
              Need something more robust?
            </h3>
            <p className="text-muted-foreground mb-8 font-default leading-relaxed">
              OceanClaw supports custom enterprise deployments, private on-premise installations, and tailored configurations for organizations that need more control.
              <span className="block mt-3 text-sm opacity-60 italic">
                Looking for specialized workflows or high-security deployments? Our team can architect a private solution for your specific requirements.
              </span>
            </p>
            <Button asChild size="lg" className="text-base px-10 rounded-xl font-default font-bold py-7">
              <Link to="#">
                Explore Enterprise
                <span className="ml-2 text-[10px] opacity-70 font-normal uppercase tracking-widest">
                  / Solutions
                </span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}